import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Search, Loader2, X } from 'lucide-react';
import { Modal, Button, SearchBar, FilterGroup, SimpleSelect, Pagination } from '@/components/shared/ui';
import { useScenarioListByOption } from '@/hooks/useScenario';
import { getScenarioCategoryList, getScenarioAdminList } from '@/services/scenarioService';
import { useApiLanguage } from '@/hooks/useApiLanguage';
import type { ScenarioAdminDto } from '@/types/api/scenario';
import { handleApiError } from '@/utils/errorHandlers';
import { getScenarioAdminListRowDisplay, mapScenarioAdminDtoToCurriculumScenarioDetail } from '@/utils/scenarioAdminToCurriculumDetail';
import { extractScenarioListCategoryRows } from '@/utils/scenarioUtils';
import type { ScenarioDetail } from '@/data/scenarioDetails';

interface AddScenarioModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (scenarios: ScenarioDetail[]) => void;
    selectedScenarioIds?: number[];
}

export default function AddScenarioModal({
    isOpen,
    onClose,
    onSelect,
    selectedScenarioIds = [],
}: AddScenarioModalProps): React.ReactElement {
    const { t, i18n } = useTranslation(['class', 'common']);
    const apiLanguage = useApiLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [tempSelectedIds, setTempSelectedIds] = useState<number[]>(selectedScenarioIds);
    const [page, setPage] = useState(1);
    const [isEnriching, setIsEnriching] = useState(false);
    const scenarioCache = useRef<Map<number, ScenarioAdminDto>>(new Map());
    const prevIsOpen = useRef(false);
    const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
    const PAGE_SIZE = 20;

    // 검색어 debounce 처리
    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(1);
        }, 500);
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [searchQuery]);

    const { data, isLoading, error } = useScenarioListByOption(
        { 
            page, 
            pageSize: PAGE_SIZE, 
            category: categoryFilter || undefined,
            search: debouncedSearch.trim() || undefined
        },
        { enabled: isOpen }
    );

    const { data: scenarioCategoryRows = [] } = useQuery({
        queryKey: ['scenario', 'categoryList', apiLanguage],
        queryFn: async () => extractScenarioListCategoryRows(await getScenarioCategoryList(undefined, apiLanguage)),
        enabled: isOpen,
        staleTime: 10 * 60 * 1000,
    });

    const categoryTitleById = useMemo(() => {
        const m = new Map<number, string>();
        for (const x of scenarioCategoryRows) {
            if (typeof x.id === 'number' && x.title) m.set(x.id, x.title);
        }
        return m;
    }, [scenarioCategoryRows]);

    const allScenarios = useMemo(
        () => (data?.scenarios ?? []).map(scenario => ({
            ...scenario,
            deviceNames: scenario.deviceTypeNames,
            contributedBy: scenario.creatorName || scenario.organizationName || '',
            scenarioCategoryList: [],
            version: '',
            difficulty: 0,
            createdAt: '',
            thumbnailImageUrl: '',
        } as ScenarioAdminDto)),
        [data]
    );

    // 모달이 **닫힘 → 열림** 으로 바뀔 때만 선택/필터/캐시 초기화, 그다음 useEffect가 현재 페이지를 캐시에 넣음
    useEffect(() => {
        if (isOpen) {
            if (!prevIsOpen.current) {
                setTempSelectedIds(selectedScenarioIds);
                setSearchQuery('');
                setDebouncedSearch('');
                setCategoryFilter('');
                setPage(1);
                scenarioCache.current = new Map();
            }
            prevIsOpen.current = true;
        } else {
            prevIsOpen.current = false;
        }
    }, [isOpen, selectedScenarioIds]);

    useEffect(() => {
        if (isOpen) {
            for (const s of allScenarios) {
                scenarioCache.current.set(s.scenarioId, s);
            }
        }
    }, [isOpen, allScenarios]);

    // /info API 호출 제거 - content 필드에 handover 정보가 포함되어 있음

    const isAllSelected =
        allScenarios.length > 0 &&
        allScenarios.every((s) => tempSelectedIds.includes(s.scenarioId));
    const isSomeSelected =
        allScenarios.some((s) => tempSelectedIds.includes(s.scenarioId)) && !isAllSelected;

    const handleToggle = (id: number) => {
        setTempSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const handleSelectAll = () => {
        const ids = allScenarios.map((s) => s.scenarioId);
        if (isAllSelected) {
            setTempSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        } else {
            setTempSelectedIds((prev) => [...new Set([...prev, ...ids])]);
        }
    };

    const resolveSelectedDtos = async (): Promise<ScenarioAdminDto[]> => {
        for (const id of tempSelectedIds) {
            if (!scenarioCache.current.has(id)) {
                const onPage = allScenarios.find((s) => s.scenarioId === id);
                if (onPage) scenarioCache.current.set(id, onPage);
            }
        }
        if (tempSelectedIds.every((id) => scenarioCache.current.has(id))) {
            return tempSelectedIds.map((id) => scenarioCache.current.get(id)!).filter(Boolean);
        }
        const bulk = await getScenarioAdminList({ page: 1, pageSize: 2000, language: apiLanguage });
        for (const s of bulk.scenarioList) {
            scenarioCache.current.set(s.scenarioId, s);
        }
        return tempSelectedIds.map((id) => {
            const c = scenarioCache.current.get(id);
            if (c) return c;
            throw new Error(`시나리오 #${id} 를 목록에서 찾을 수 없습니다.`);
        });
    };

    const handleConfirm = async () => {
        setIsEnriching(true);
        try {
            const selectedDtos = await resolveSelectedDtos();
            if (!selectedDtos.length) {
                onClose();
                return;
            }
            const results: ScenarioDetail[] = selectedDtos.map((s) => 
                mapScenarioAdminDtoToCurriculumScenarioDetail(s, null, i18n.language)
            );
            onSelect(results);
            onClose();
        } catch (e) {
            window.alert(
                e instanceof Error ? e.message : '시나리오를 불러오는 데 실패했습니다. 잠시 후 다시 시도해 주세요.',
            );
        } finally {
            setIsEnriching(false);
        }
    };

    const handleCancel = () => {
        setTempSelectedIds(selectedScenarioIds);
        setSearchQuery('');
        setDebouncedSearch('');
        setCategoryFilter('');
        onClose();
    };

    const totalCount = data?.total ?? 0;
    const totalPages = data?.totalPages ?? Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    return (
        <Modal isOpen={isOpen} onClose={handleCancel} title={t('scenarioModal.title')} size="xl">
            <div className="flex flex-col h-full max-h-[calc(90vh-120px)]">
                <div className="flex-shrink-0 bg-white z-10">
                    <FilterGroup>
                        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md border border-gray-200 whitespace-nowrap">
                            <input
                                type="checkbox"
                                checked={isAllSelected}
                                ref={(el) => {
                                    if (el) el.indeterminate = isSomeSelected;
                                }}
                                onChange={handleSelectAll}
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">{t('scenarioModal.all')}</span>
                            <span className="text-sm text-gray-500">({tempSelectedIds.length})</span>
                        </div>
                        <div className="w-44 flex-shrink-0">
                            <SimpleSelect
                                value={categoryFilter}
                                onChange={(e) => {
                                    setCategoryFilter(e.target.value);
                                    setPage(1);
                                }}
                            >
                                <option value="">{t('scenarioModal.allCategories')}</option>
                                {scenarioCategoryRows.map((opt) => (
                                    <option key={opt.id} value={String(opt.id)}>
                                        {opt.title}
                                    </option>
                                ))}
                            </SimpleSelect>
                        </div>
                        <div className="flex-1">
                            <SearchBar
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder={t('scenarioModal.searchPlaceholder')}
                            />
                        </div>
                    </FilterGroup>
                </div>

                <div className="flex-1 overflow-y-auto min-h-0 my-4">
                    {isLoading ? (
                        <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
                            <Loader2 size={20} className="animate-spin" />
                            <span className="text-sm">시나리오 목록을 불러오는 중...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-500 text-sm px-2">
                            {handleApiError(error, '시나리오 목록을 불러오지 못했습니다.').message}
                        </div>
                    ) : allScenarios.length > 0 ? (
                        <div className="space-y-2">
                            {allScenarios.map((scenario) => {
                                const isSelected = tempSelectedIds.includes(scenario.scenarioId);
                                const row = getScenarioAdminListRowDisplay(
                                    scenario,
                                    null,
                                    i18n.language,
                                    { categoryNameById: (id) => categoryTitleById.get(id) }
                                );
                                return (
                                    <div
                                        key={scenario.scenarioId}
                                        onClick={() => handleToggle(scenario.scenarioId)}
                                        className={`px-3 py-2.5 border rounded-md cursor-pointer transition-all ${
                                            isSelected
                                                ? 'border-blue-500 bg-blue-50'
                                                : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="mt-0.5">
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => {}}
                                                    className="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-400"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0 flex items-start justify-between gap-3 flex-wrap">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <h4 className="font-semibold text-sm text-gray-900">
                                                            {scenario.title}
                                                        </h4>
                                                        {scenario.modeValueTitle && (
                                                            <span className="inline-flex items-center px-[3px] py-[1px] rounded text-[11px] font-medium bg-sky-50 text-sky-700 border border-sky-300 leading-tight">
                                                                {scenario.modeValueTitle}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {scenario.content ? (
                                                        <p className="text-xs text-gray-600 leading-relaxed line-clamp-3 break-words mt-1.5">
                                                            {scenario.content}
                                                        </p>
                                                    ) : null}
                                                </div>
                                                <div className="flex items-center flex-shrink-0 gap-1.5 flex-wrap justify-end text-xs max-w-full">
                                                    <span
                                                        className="inline-flex max-w-[8.5rem] sm:max-w-[10rem] truncate items-center justify-center border border-sky-200/90 bg-sky-50/90 text-sky-900 font-medium px-2 py-0.5 rounded-md"
                                                        title={t('scenarioModal.chipCategory') + (row.categoryPart && row.categoryPart !== '—' ? `: ${row.categoryPart}` : '')}
                                                    >
                                                        {row.categoryPart}
                                                    </span>
                                                    <span
                                                        className="inline-flex max-w-[8.5rem] sm:max-w-[10rem] truncate items-center justify-center border border-sky-200/90 bg-sky-50/90 text-sky-900 font-medium px-2 py-0.5 rounded-md"
                                                        title={row.author}
                                                    >
                                                        {row.author}
                                                    </span>
                                                    <span className="inline-flex min-w-[2.25rem] items-center justify-center border border-sky-200/90 bg-sky-50/90 text-sky-900 font-medium px-2 py-0.5 rounded-md">
                                                        {row.minutesPart}
                                                    </span>
                                                    <span className="inline-flex min-w-[2.4rem] items-center justify-center border border-sky-200/90 bg-sky-50/90 text-sky-900 font-medium px-2 py-0.5 rounded-md text-center">
                                                        {row.platform}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <Search size={48} className="mx-auto text-gray-300 mb-3" />
                            <p className="text-gray-500">{t('scenarioModal.noResults')}</p>
                        </div>
                    )}
                </div>

                {tempSelectedIds.length > 0 && (
                    <div className="flex-shrink-0 border-t border-gray-100 pt-2">
                        <h5 className="text-sm font-medium text-gray-700 mb-1.5">
                            선택한 시나리오 ({tempSelectedIds.length}개)
                        </h5>
                        <div className="flex flex-wrap gap-2 max-h-[60px] overflow-y-auto">
                            {tempSelectedIds.map((id) => {
                                const scenario = scenarioCache.current.get(id) || allScenarios.find(s => s.scenarioId === id);
                                const title = scenario?.title || `시나리오 #${id}`;
                                return (
                                    <div
                                        key={id}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900"
                                    >
                                        <span className="max-w-[200px] truncate">{title}</span>
                                        <button
                                            type="button"
                                            onClick={() => handleToggle(id)}
                                            className="flex-shrink-0 hover:bg-blue-100 rounded p-0.5 transition-colors"
                                        >
                                            <X size={14} className="text-blue-700" />
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex-shrink-0 flex items-center pt-4 border-t border-gray-200 bg-white">
                    <div className="flex-1"></div>
                    <div className="flex items-center justify-center">
                        {totalCount > 0 && totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalCount}
                                onPageChange={setPage}
                                className="mt-0"
                            />
                        )}
                    </div>
                    <div className="flex-1 flex items-center justify-end gap-3">
                        <Button variant="secondary" onClick={handleCancel}>
                            {t('scenarioModal.cancel')}
                        </Button>
                        <Button variant="primary" onClick={() => void handleConfirm()} disabled={!!error || isLoading || isEnriching}>
                            {isEnriching ? (
                                <span className="inline-flex items-center gap-2">
                                    <Loader2 size={16} className="animate-spin" />
                                    {t('scenarioModal.confirm')} ({tempSelectedIds.length})
                                </span>
                            ) : (
                                <>
                                    {t('scenarioModal.confirm')} ({tempSelectedIds.length})
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
